/**
 * "Expand" — give one panel every pixel the stage can spare.
 *
 * A list panel (Objects, Events, Motion, Video search) is `fill: false`, so it
 * holds the width it was given while camera_view soaks up the rest. Reading a
 * wide gallery therefore means dismissing the viewer and dragging the edge
 * back out; Expand is that pair of gestures as one control.
 *
 * Panels to the LEFT are left alone — they are the navigation the user chose
 * to keep, and `selectVisible` keeps a left-to-right prefix, so claiming their
 * width would drop the expanding panel itself rather than them. Everything to
 * the RIGHT closes, and the panel is saved at MAX_PANEL_WIDTH: it fills the row
 * whenever it is on the stage, until a drag on its edge gives it a width.
 */

import { clamp } from '@/lib/utils';
import type { AllocatedPanel, PanelId, UserWidths } from '@/types/panels';

import { DEFAULT_PANEL_ORDER, getPanelWidthBudget } from './allocate';

/** What Expand would do: the width to take, and what closes to free it. */
interface ExpansionPlan {
    widthPx: number;
    closeIds: PanelId[];
    canExpand: boolean;
}

// Sub-pixel tolerance: allocated widths are floats, and a panel already at its
// maximum must not offer a control that would do nothing.
const EPS = 0.5;

/**
 * Plan the expansion of `panelId` within an allocated row (PanelStage's
 * `items`). Returns null when the panel is not in the row.
 *
 * `canExpand` is false once the panel already owns everything left of it —
 * whether because nothing is open to its right, or because its own `max`
 * stops it from growing into the space that is there.
 */
/** The parts of an allocated slot a plan reads. */
type ExpansionSlot = Pick<AllocatedPanel, 'id' | 'widthPx' | 'min' | 'max'>;

export function planExpansion(
    items: ExpansionSlot[], stageWidth: number, panelId: PanelId,
): ExpansionPlan | null {
    const index = items.findIndex((item) => item.id === panelId);
    if (index < 0) {
        return null;
    }

    const panel = items[index];
    if (panel === undefined) { return null; }
    const itemsBefore = items.slice(0, index);
    const leftWidth = itemsBefore.reduce((sum, item) => sum + item.widthPx, 0);
    // The row Expand would leave behind: the panels to the left, plus this one.
    const budget = getPanelWidthBudget(stageWidth, index + 1);
    const widthPx = clamp(budget - leftWidth, panel.min, panel.max);

    return {
        widthPx,
        closeIds: items.slice(index + 1).map((item) => item.id),
        canExpand: widthPx > panel.widthPx + EPS,
    };
}

/**
 * The width of `panelId` after it grows by `byPx` into the free room at the right end of the
 * row, or null when that room or the panel's own `max` cannot hold all of it. No other panel
 * closes or narrows, so a row with a `fill` panel or an expanded panel has no free room.
 */
export function planWidening(
    items: ExpansionSlot[], stageWidth: number, panelId: PanelId, byPx: number,
): number | null {
    const panel = items.find((item) => item.id === panelId);
    if (panel === undefined || byPx <= 0) { return null; }
    const used = items.reduce((sum, item) => sum + item.widthPx, 0);
    const freePx = getPanelWidthBudget(stageWidth, items.length) - used;
    const widthPx = panel.widthPx + byPx;
    return byPx <= freePx + EPS && widthPx <= panel.max + EPS ? widthPx : null;
}

/** The parts of an allocated slot a docking plan reads. */
type DockingSlot = Pick<AllocatedPanel, 'id' | 'widthPx' | 'min' | 'fill' | 'config'>;

const getOrder = (slot: DockingSlot): number => slot.config.order ?? DEFAULT_PANEL_ORDER;

// Float noise only: `selectVisible` docks the overlay only when the row holds it exactly.
const FIT_EPS = 1e-6;

/**
 * The widths of the panels to the left of a floating overlay that let it dock: the widths they
 * show now, with the nearest narrowed first, each down to its min, by the room the overlay needs.
 * The row then has room for the overlay at its own width, and `selectVisible` docks it.
 *
 * Null when the panels reach their minimums first, or the row holds a `fill` panel, which gives
 * up width to any panel beside it.
 */
export function planDocking(
    items: DockingSlot[], overlay: DockingSlot, stageWidth: number,
): UserWidths | null {
    if (items.some((item) => item.fill)) { return null; }
    const used = items.reduce((sum, item) => sum + item.widthPx, overlay.widthPx);
    let deficit = used - getPanelWidthBudget(stageWidth, items.length + 1);
    const widths: UserWidths = {};
    for (const slot of items.filter((item) => getOrder(item) < getOrder(overlay)).reverse()) {
        const take = Math.max(0, Math.min(deficit, slot.widthPx - slot.min));
        // Whole pixels, rounded down, so the row stays inside the budget.
        widths[slot.id] = Math.floor(slot.widthPx - take + FIT_EPS);
        deficit -= take;
    }
    return deficit > FIT_EPS ? null : widths;
}
